import React, { Component } from 'react';
import { Modal, Table, Button, message, Spin, Alert, Tag, Tooltip } from 'antd';
import { FilePdfOutlined, DownloadOutlined, EyeOutlined, CheckCircleOutlined, ClockCircleOutlined } from '@ant-design/icons';
import { getProjectTbtRuns, downloadProjectTbtPdf } from '../../HttpRequests/toolboxTalks';
import PropTypes from 'prop-types';

class TBTProjectReportsModal extends Component {
  constructor(props) {
    super(props);
    this.state = {
      visible: false,
      loading: false,
      tbtRuns: [],
      downloading: {},
      project: null,
    };
  }

  show = async (project) => {
    this.setState({ 
      visible: true, 
      project,
      loading: true 
    });
    
    try {
      const response = await getProjectTbtRuns(project.id);
      this.setState({ 
        tbtRuns: response.data.tbt_runs || [],
        project: response.data.project
      });
    } catch (error) {
      console.error('Failed to load TBT runs:', error);
      message.error('Failed to load TBT runs for this project');
    } finally {
      this.setState({ loading: false });
    }
  };

  hide = () => {
    this.setState({
      visible: false,
      tbtRuns: [],
      project: null,
      downloading: {},
    });
  };

  handleDownloadPdf = async (run) => {
    const { project } = this.state;
    
    this.setState(prevState => ({
      downloading: { ...prevState.downloading, [run.id]: true }
    }));

    try {
      const response = await downloadProjectTbtPdf(run.id, project.id);
      
      // Create blob and download
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `TBT_Project_${project.number}_${run.run_at.split('T')[0]}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      message.success('TBT report downloaded successfully');
    } catch (error) {
      console.error('Failed to download TBT PDF:', error);
      message.error('Failed to download TBT report');
    } finally {
      this.setState(prevState => ({
        downloading: { ...prevState.downloading, [run.id]: false }
      }));
    }
  };

  getStatusTag = (run) => {
    const completionRate = run.total_signed / Math.max(run.subcontractors.length, 1);
    
    if (completionRate >= 1) {
      return <Tag icon={<CheckCircleOutlined />} color="success">Complete</Tag>;
    } else if (completionRate >= 0.5) {
      return <Tag icon={<ClockCircleOutlined />} color="processing">In Progress</Tag>;
    } else {
      return <Tag icon={<ClockCircleOutlined />} color="default">Pending</Tag>;
    }
  };

  render() {
    const { visible, loading, tbtRuns, project, downloading } = this.state;

    const columns = [
      {
        title: 'Run Date',
        dataIndex: 'run_at',
        key: 'run_at',
        render: (date) => new Date(date).toLocaleDateString('en-GB', {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric',
          hour: '2-digit',
          minute: '2-digit'
        }),
        sorter: (a, b) => new Date(a.run_at) - new Date(b.run_at),
        defaultSortOrder: 'descend',
      },
      {
        title: 'Status',
        key: 'status',
        render: (_, run) => this.getStatusTag(run),
      },
      {
        title: 'Recipients',
        key: 'recipients',
        render: (_, run) => (
          <div>
            <div>{run.subcontractors.length} subcontractors</div>
            <div style={{ fontSize: '11px', color: '#666' }}>
              {run.total_signed} signed • {run.total_viewed} viewed
            </div>
          </div>
        ),
      },
      {
        title: 'Completion',
        key: 'completion',
        render: (_, run) => {
          const rate = (run.total_signed / Math.max(run.subcontractors.length, 1)) * 100;
          return (
            <div>
              <div style={{ fontWeight: 'bold' }}>{rate.toFixed(1)}%</div>
              <div style={{ fontSize: '11px', color: '#666' }}>
                {run.total_signed}/{run.subcontractors.length}
              </div>
            </div>
          );
        },
        sorter: (a, b) => {
          const rateA = (a.total_signed / Math.max(a.subcontractors.length, 1)) * 100;
          const rateB = (b.total_signed / Math.max(b.subcontractors.length, 1)) * 100;
          return rateA - rateB;
        },
      },
      {
        title: 'Subcontractors',
        key: 'subcontractors',
        render: (_, run) => (
          <Tooltip
            title={
              <div>
                {run.subcontractors.map(sub => (
                  <div key={sub.id} style={{ marginBottom: '4px' }}>
                    <strong>{sub.name}</strong>
                    {sub.group && ` (${sub.group})`}
                    <br />
                    <small>
                      Jobs: {sub.jobs.map(job => job.description).join(', ')}
                    </small>
                    <br />
                    <small style={{ color: sub.signed_at ? '#52c41a' : '#faad14' }}>
                      {sub.signed_at ? `Signed: ${new Date(sub.signed_at).toLocaleDateString()}` : 
                       sub.viewed_at ? `Viewed: ${new Date(sub.viewed_at).toLocaleDateString()}` : 'Pending'}
                    </small>
                  </div>
                ))}
              </div>
            }
            overlayStyle={{ maxWidth: '400px' }}
          >
            <Button 
              type="link" 
              icon={<EyeOutlined />}
              size="small"
            >
              View Details
            </Button>
          </Tooltip>
        ),
      },
      {
        title: 'Action',
        key: 'action',
        render: (_, run) => (
          <Button
            type="primary"
            icon={<DownloadOutlined />}
            onClick={() => this.handleDownloadPdf(run)}
            loading={downloading[run.id]}
            disabled={downloading[run.id]}
            size="small"
          >
            {downloading[run.id] ? 'Downloading...' : 'Download PDF'}
          </Button>
        ),
      },
    ];

    return (
      <Modal
        title={
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <FilePdfOutlined style={{ marginRight: 8, color: '#1890ff' }} />
            TBT Reports - {project ? `Project ${project.number}` : 'Loading...'}
          </div>
        }
        visible={visible}
        onCancel={this.hide}
        width={1000}
        footer={[
          <Button key="close" onClick={this.hide}>
            Close
          </Button>,
        ]}
      >
        {project && (
          <Alert
            message={`TBT Reports for Project ${project.number}`}
            description={`${project.title} - Showing TBT runs with subcontractors booked on jobs for this project`}
            type="info"
            showIcon
            style={{ marginBottom: 20 }}
          />
        )}

        <Spin spinning={loading}>
          {tbtRuns.length === 0 && !loading ? (
            <Alert
              message="No TBT Reports Found"
              description="No TBT runs have been created for subcontractors working on this project, or no subcontractors are currently booked on jobs for this project."
              type="warning"
              showIcon
            />
          ) : (
            <Table
              columns={columns}
              dataSource={tbtRuns}
              rowKey="id"
              pagination={{
                pageSize: 10,
                showSizeChanger: true,
                showQuickJumper: true,
                showTotal: (total, range) => 
                  `${range[0]}-${range[1]} of ${total} TBT runs`,
              }}
              scroll={{ x: 800 }}
            />
          )}
        </Spin>

        <div style={{ marginTop: 16, fontSize: '12px', color: '#666' }}>
          <strong>Note:</strong> Only TBT runs that include subcontractors with active jobs on this project are shown. 
          PDF reports contain all TBT documents and signatures for the selected run.
        </div>
      </Modal>
    );
  }
}

TBTProjectReportsModal.propTypes = {
  // No props needed - modal is controlled via ref methods
};

export default TBTProjectReportsModal;
